import { useEffect, useState, useMemo, useCallback } from "react";
import "./index.css";
import { requestGeolocation, startHeightSync, trackEvent, fuelAction } from "./iframe-bridge";

// Compact format: [brand, addr, muni, lat, lon, p95, diesel, lpg]
type Row = [string, string, string, number | null, number | null, number | null, number | null, number | null];
type Fuel = "p95" | "diesel" | "lpg";
type FuelAvg = { p95: number | null; diesel: number | null; lpg: number | null };

const FUEL_IDX: Record<Fuel, number> = { p95: 5, diesel: 6, lpg: 7 };
const FUEL_LABELS: Record<Fuel, string> = { p95: "95 benzinas", diesel: "Dyzelinas", lpg: "Dujos" };
const DEFAULT_CITY = "Vilniaus m. sav.";

const fmt = (n: number) => n.toFixed(3).replace(".", ",");
const addr = (r: Row) => r[1].includes(",") ? r[1] : `${r[2]}, ${r[1]}`;

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function findNearestMuni(lat: number, lon: number, rows: Row[]): string {
  let bestMuni = DEFAULT_CITY;
  let bestDist = Infinity;
  for (const r of rows) {
    if (r[3] == null || r[4] == null) continue;
    const d = haversineKm(lat, lon, r[3], r[4]);
    if (d < bestDist) {
      bestDist = d;
      bestMuni = r[2];
    }
  }
  return bestMuni;
}

// Short display name: "Vilniaus m. sav." → "Vilniaus", "Kauno r. sav." → "Kauno r."
const cityShort = (muni: string) => muni.replace(/ m\. sav\.$/, "").replace(/ r\. sav\.$/, " r.").replace(/ sav\.$/, "");

export function Widget() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [reportDate, setReportDate] = useState("");
  const [fuel, setFuel] = useState<Fuel>("p95");
  const [city, setCity] = useState(DEFAULT_CITY);
  const [prevAvg, setPrevAvg] = useState<FuelAvg | null>(null);
  const [prevMuniAvg, setPrevMuniAvg] = useState<Record<string, FuelAvg> | null>(null);
  const [locLoading, setLocLoading] = useState(false);
  const [locDetected, setLocDetected] = useState(false);

  useEffect(() => {
    fetch("/data/widget.json")
      .then(r => r.json())
      .then((d: { s: Row[]; d: string; pa: FuelAvg | null; pma: Record<string, FuelAvg> | null }) => {
        setRows(d.s);
        setReportDate(d.d);
        setPrevAvg(d.pa);
        setPrevMuniAvg(d.pma);
      });
  }, []);

  useEffect(() => startHeightSync(), []);

  const handleLocate = useCallback(() => {
    if (!rows) return;
    trackEvent("widget", "filter_location");
    setLocLoading(true);
    requestGeolocation(
      (coords) => {
        const nearest = findNearestMuni(coords.latitude, coords.longitude, rows);
        setCity(nearest);
        setLocDetected(true);
        setLocLoading(false);
      },
      () => { setLocLoading(false); },
    );
  }, [rows]);

  const resetCity = useCallback(() => {
    setCity(DEFAULT_CITY);
    setLocDetected(false);
  }, []);

  const fi = FUEL_IDX[fuel];

  const allWithFuel = useMemo(() => {
    if (!rows) return [];
    return rows.filter(r => r[fi] != null);
  }, [rows, fi]);

  const natAvg = useMemo(() => {
    if (allWithFuel.length === 0) return null;
    return allWithFuel.reduce((t, r) => t + (r[fi] as number), 0) / allWithFuel.length;
  }, [allWithFuel, fi]);

  const cityStations = useMemo(() => {
    return allWithFuel
      .filter(r => r[2] === city)
      .sort((a, b) => (a[fi] as number) - (b[fi] as number));
  }, [allWithFuel, fi, city]);

  const cityAvg = useMemo(() => {
    if (cityStations.length === 0) return null;
    return cityStations.reduce((t, r) => t + (r[fi] as number), 0) / cityStations.length;
  }, [cityStations, fi]);

  const delta = useMemo(() => {
    if (cityAvg == null) return null;
    const prev = prevMuniAvg?.[city]?.[fuel] ?? null;
    if (prev == null) return null;
    return cityAvg - prev;
  }, [cityAvg, prevMuniAvg, fuel, city]);

  const cheapestInCity = useMemo(() => cityStations.slice(0, 3), [cityStations]);

  if (!rows) {
    return (
      <div className="w-[970px] h-[400px] bg-black flex items-center justify-center text-grey-60 font-body">
        Kraunama...
      </div>
    );
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("lt-LT", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  return (
    <div className="font-body w-[970px] h-[400px] bg-black text-[#e5e5e5] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex px-5 pt-3 pb-2 gap-3">
        <div className="flex-1 flex items-center">
          <h1 className="font-display font-bold text-[24px] leading-none m-0 text-white whitespace-nowrap">Degalų kainos Lietuvoje</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 min-h-0 overflow-hidden">
        <div className="h-full grid grid-cols-2 gap-3">
          {/* Left — average price card */}
          <div className="min-w-0 bg-[#047FFE] p-4 flex flex-col text-white">
            <div className="grid grid-cols-3 mb-4">
              {(["p95", "diesel", "lpg"] as Fuel[]).map(f => (
                <button
                  key={f}
                  onClick={() => { trackEvent("widget", fuelAction(f)); setFuel(f); }}
                  className={`py-1.5 px-4 border text-[13px] font-semibold cursor-pointer transition-all duration-150
                    ${fuel === f
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-white border-white/40 hover:border-white"}`}
                >
                  {FUEL_LABELS[f]}
                </button>
              ))}
            </div>
            <div className="flex-1 flex flex-col justify-center">
            <div className="text-[11px] uppercase tracking-wider font-semibold mb-1 opacity-80">
              {FUEL_LABELS[fuel]} · {cityShort(city)} sav.
            </div>
            <div className="flex items-end justify-between gap-3">
              <div className="font-display font-bold text-[80px] leading-[0.9] tracking-tight">
                {cityAvg != null ? fmt(cityAvg) : "—"}
                <span className="text-[0.35em] font-bold opacity-70"> €/l vidurkis</span>
              </div>
              {delta != null && Math.abs(delta) >= 0.0005 && (
                <span className={`font-body font-bold text-[13px] py-1.5 px-3 rounded-full whitespace-nowrap inline-flex items-center gap-1
                  ${delta > 0
                    ? "bg-[#ffe539] text-black"
                    : "bg-white/20 text-white border border-white/40"}`}
                >
                  {delta > 0 ? "↑" : "↓"} {fmt(Math.abs(delta))} €
                </span>
              )}
            </div>
            {natAvg != null && (
              <div className="mt-1 text-[14px] opacity-70">
                Lietuvoje vidurkis: <b className="font-semibold opacity-100">{fmt(natAvg)} €/l</b>
              </div>
            )}
            </div>
          </div>
          {/* Right — cheapest stations + link */}
          <div className="min-w-0 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-grey-60">Mažiausia kaina</div>
              {locDetected ? (
                <button
                  onClick={resetCity}
                  className="py-1 px-3 border border-grey-40 bg-transparent text-white text-[12px] font-semibold cursor-pointer hover:border-white transition-all duration-150 whitespace-nowrap shrink-0"
                >
                  ✕ {cityShort(city)}
                </button>
              ) : (
                <button
                  onClick={handleLocate}
                  disabled={locLoading}
                  className="py-1 px-3 border-2 border-white bg-white text-black text-[12px] font-semibold cursor-pointer hover:bg-grey-80 hover:text-white transition-all duration-150 disabled:opacity-60 whitespace-nowrap shrink-0"
                >
                  {locLoading ? "..." : "📍 Šalia manęs"}
                </button>
              )}
            </div>
            {cheapestInCity.map((r) => (
              <div key={`${r[0]}|${r[1]}`} className="flex-1 bg-[#1a1a1a] px-4 py-2 flex flex-col justify-center">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[18px] font-semibold text-white truncate">{r[0]}</div>
                    <div className="text-[16px] text-grey-60 truncate mt-0.5">{addr(r)}</div>
                  </div>
                  <div className="font-display font-bold text-[28px] text-white leading-none whitespace-nowrap">
                    {fmt(r[fi]!)}
                    <span className="text-[12px] text-grey-60 ml-0.5">€/l</span>
                  </div>
                </div>
              </div>
            ))}
            <a
              href="https://www.15min.lt/projektas/degalu-kainos"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("widget", "more")}
              className="bg-[#047FFE] text-white text-[14px] font-semibold py-2 px-4 no-underline text-center leading-none flex items-center justify-center hover:bg-[#0366d6] transition-colors"
            >
              Daugiau {"\u2192"}
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-2">
        <span className="text-[10px] text-grey-60">Duomenys atnaujinti: {formatDate(reportDate)} 10:00 · Atnaujinama kasdien · Šaltinis: LEA (Lietuvos energetikos agentūra)</span>
      </div>
    </div>
  );
}

export default Widget;
